/**
 * Injected by the extension UI via Global; also exported for consumers who inject styles elsewhere.
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Extension container override requires :has and !important
import { css, type SerializedStyles } from '@emotion/react';

import { token } from '@atlaskit/tokens';

const wrapGap = token('space.150', '12px');
const noWrapGap = token('space.300', '24px');

/* eslint-disable @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/ui-styling-standard/no-important-styles -- Styles injected via Global; !important overrides extension inline width */
export const nativeEmbedAlignmentStyles: SerializedStyles = css({
	// Remove margin on extension-overflow-wrapper when it contains a native embed to prevent width overflow.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target overflow wrapper by data attribute
	'.extension-overflow-wrapper:has([data-native-embed-alignment])': {
		margin: '0 !important',
	},
	// The renderer sets overflow-x:auto on .ak-renderer-extension-overflow-container, which clips a
	// pixel-resized native embed that is wider than the text column and prevents centering from
	// working (the scroll container also becomes the reference for the 50% offset). Override to
	// visible so the embed can break out symmetrically on both sides, exactly as images do.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target overflow container by data attribute
	'.ak-renderer-extension-overflow-container:has([data-native-embed-alignment])': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- !important string requires type cast incompatible with the CSSProperties type
		overflowX: 'visible !important' as 'visible',
	},
	// For a center-aligned embed with an explicit pixel width, the outer .ak-renderer-extension
	// wrapper's width is set directly as an inline style by the native embed useEffect (to override
	// the renderer's own inline style="width:100%"). These CSS rules apply the centering transforms
	// on that same wrapper — now that it has the exact pixel width, margin-left:50% +
	// translateX(-50%) breaks it out symmetrically, exactly as MediaSingle does for images.
	// max-width:100cqw prevents overflow beyond the container while still allowing
	// the embed to break out of the narrow layout column (unlike 100% which would
	// clamp to the ~760px parent).
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target renderer extension wrapper by data attribute
	'.ak-renderer-extension:has([data-native-embed-alignment="center"]):has([data-native-embed-width])':
		{
			position: 'relative',
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space -- geometric centering offset, not a spacing value
			marginLeft: '50%',
			transform: 'translateX(-50%)',
			maxWidth: '100cqw',
		},
	// Width: 100% when no explicit width set; fit-content when data-native-embed-width is set.
	// Combined selectors cover both .extension-container (editor) and .ak-renderer-extension (renderer)
	// which use different wrapper class names for the same structural element.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target extension wrappers by data attribute
	'.extension-container:has([data-native-embed-alignment]):not(:has([data-native-embed-width])), .ak-renderer-extension:has([data-native-embed-alignment]):not(:has([data-native-embed-width]))':
		{
			width: '100% !important',
			marginLeft: '0 !important',
			marginRight: '0 !important',
			paddingLeft: '0 !important',
			paddingRight: '0 !important',
		},
	// Match editor/renderer wrapper clipping and selected-node ring to the native embed frame radius.
	// Borderless experiences keep the smaller core wrapper radius so their iframe content is not clipped
	// to a framed shape they do not visually own.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target extension wrappers by the resolved showBorder value
	'.extension-container:has([data-native-embed-show-border="true"]), .ak-renderer-extension:has([data-native-embed-show-border="true"])':
		{
			borderRadius: token('radius.xlarge'),
		},
	// Initial render placeholder: reserve space with chrome matching the real native embed wrapper
	// while the async iframe experience is resolving. Width/height/aspect-ratio remain inline because
	// they are derived from ADF params and manifest defaults at runtime.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target native embed initial placeholder by test id and border marker
	'[data-native-embed-show-border="true"] > [data-testid="native-embed-initial-placeholder"]': {
		boxSizing: 'border-box',
		maxWidth: '100%',
		overflow: 'hidden',
		backgroundColor: token('elevation.surface.sunken'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
	},
	// Borderless experiences such as MAUI/databases intentionally keep the smaller clipping radius
	// used by the real borderless iframe wrapper and avoid painting placeholder border chrome.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target native embed initial placeholder by test id and border marker
	'[data-native-embed-show-border="false"] > [data-testid="native-embed-initial-placeholder"]': {
		boxSizing: 'border-box',
		maxWidth: '100%',
		overflow: 'hidden',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.small'),
	},
	// In the editor, the --native-embed-width CSS variable is set on .extension-container by the
	// resize plugin. When the embed is nested inside another node (e.g. a table cell or expand),
	// data-native-embed-nested is present and we use maxWidth so the embed shrinks with the parent.
	// When the embed is top-level (not nested), we use width so it takes the exact pixel size.
	// In the renderer, width is set directly as an inline style on .ak-renderer-extension (overriding
	// the renderer's own inline style="width:100%"), so no CSS variable rule is needed for the renderer.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target nested extension container by data attribute on the container itself
	'.extension-container[data-native-embed-nested]:has([data-native-embed-alignment]):has([data-native-embed-width])':
		{
			maxWidth: 'var(--native-embed-width) !important',
			paddingLeft: '0 !important',
			paddingRight: '0 !important',
		},
	// The second selector adds an ID-level specificity bump so this rule also wins in full-width mode
	// (Confluence "wide" page width), where the editor applies `width: 100% !important` to
	// `.extension-container.block` via a rule scoped under
	// `.fabric-editor--full-width-mode:not(:has(#chromeless-editor))` with specificity (1, 3, 0).
	// Without the second selector, the native embed's (0, 3, 0) rule loses the cascade battle.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Second selector matches specificity of editor-core's full-width-mode rule; own ID won't work as our div is inside .extension-container, not an ancestor
	'.extension-container:not([data-native-embed-nested]):has([data-native-embed-alignment]):has([data-native-embed-width]), .fabric-editor--full-width-mode:not(:has(#chromeless-editor)) .extension-container.block:not([data-native-embed-nested]):has([data-native-embed-alignment]):has([data-native-embed-width])':
		{
			width: 'var(--native-embed-width) !important',
			paddingLeft: '0 !important',
			paddingRight: '0 !important',
		},
	// Layout column resizing can temporarily rebuild column content before the
	// native embed plugin re-syncs data-native-embed-nested. Fall back to the
	// actual layout-column DOM ancestry so an embed in a column still shrinks to
	// the column instead of taking its stored pixel width.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- DOM ancestry fallback for transient missing data-native-embed-nested
	'.ProseMirror [data-layout-column] .extension-container:has([data-native-embed-alignment]):has([data-native-embed-width]), .fabric-editor--full-width-mode:not(:has(#chromeless-editor)) [data-layout-column] .extension-container.block:has([data-native-embed-alignment]):has([data-native-embed-width])':
		{
			width: 'auto !important',
			maxWidth: 'var(--native-embed-width) !important',
			paddingLeft: '0 !important',
			paddingRight: '0 !important',
		},
	// Override the extension wrapper's width so alignment margins take effect.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target extension wrappers by data attribute
	'.extension-container:has([data-native-embed-alignment="left"]), .ak-renderer-extension:has([data-native-embed-alignment="left"])':
		{
			margin: `${noWrapGap} auto ${noWrapGap} 0 !important`,
			overflow: 'auto',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target extension wrappers by data attribute
	'.extension-container:has([data-native-embed-alignment="center"]), .ak-renderer-extension:has([data-native-embed-alignment="center"])':
		{
			margin: `${noWrapGap} 0 !important`,
			position: 'relative',
			left: '50%',
			transform: 'translateX(-50%)',
			marginLeft: '0 !important',
			marginRight: '0 !important',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target extension wrappers by data attribute
	'.extension-container:has([data-native-embed-alignment="right"]), .ak-renderer-extension:has([data-native-embed-alignment="right"])':
		{
			margin: `${noWrapGap} 0 ${noWrapGap} auto !important`,
			overflow: 'auto',
		},
	// wrap-left / wrap-right: float + margin like MediaSingle so text wraps around the embed.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target extension wrappers by data attribute
	'.extension-container:has([data-native-embed-alignment="wrap-left"])': {
		float: 'left',
		margin: `${wrapGap} ${wrapGap} ${wrapGap} auto !important`,
		maxWidth: `calc((var(--ak-editor-max-container-width, 100%) / 2) - ${wrapGap})`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target extension wrappers by data attribute
	'.ak-renderer-extension:has([data-native-embed-alignment="wrap-left"])': {
		float: 'left',
		margin: `${wrapGap} ${wrapGap} ${wrapGap} auto !important`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target extension wrappers by data attribute
	'.extension-container:has([data-native-embed-alignment="wrap-right"])': {
		float: 'right',
		margin: `${wrapGap} auto ${wrapGap} ${wrapGap} !important`,
		maxWidth: `calc((var(--ak-editor-max-container-width, 100%) / 2) - ${wrapGap})`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target extension wrappers by data attribute
	'.ak-renderer-extension:has([data-native-embed-alignment="wrap-right"])': {
		float: 'right',
		margin: `${wrapGap} auto ${wrapGap} ${wrapGap} !important`,
	},
	// Suppress the hover box-shadow on the extension container for all native embeds.
	// Native embeds provide their own chrome so the generic extension hover ring is not appropriate.
	// The selection ring (from .ak-editor-selected-node) is also applied as a box-shadow,
	// and would be incorrectly hidden on hover without the :not() guard.
	// Using :not(.ak-editor-selected-node *) avoids coupling to the intermediate DOM structure.
	// In the renderer there is no selection ring so the :not() guard is not needed.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target extension wrapper hover by data attribute
	'.extension-container:has([data-native-embed-alignment]):not(.ak-editor-selected-node *):hover': {
		boxShadow: 'none !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target renderer extension wrapper hover by data attribute
	'.ak-renderer-extension:has([data-native-embed-alignment]):hover': {
		boxShadow: 'none !important',
	},
	// Suppress the selection ring (box-shadow from .ak-editor-selected-node) for database embeds.
	// Database embeds use appearance="minimal" with no content border, so the selection ring
	// creates a jarring visual effect around a borderless embed.
	// The box-shadow is applied by editor-core to `.extension-container` via the selector:
	//   `.extensionView-content-wrap.ak-editor-selected-node > span > .extension-container`
	// We target `.extension-container` directly using :has() to check for the experience attribute
	// on the inner div, matching regardless of the intermediate DOM structure.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target extension-container for database embeds when selected
	'.extension-container:has([data-native-embed-experience="database"])': {
		boxShadow: 'none !important',
	},
});

export const nativeEmbedResizeShellStyles: SerializedStyles = css({
	// The extension-owned resize shell keeps the handles in the native embed DOM, so the editor's
	// generic extension scroll wrapper needs a narrow aperture around the resize shell. Without this,
	// the outside handles are either clipped or exposed through a horizontal scrollbar.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target resize shell marker to avoid changing generic extension overflow
	'.extension-overflow-wrapper:has([data-native-embed-resizer-shell="true"])': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- !important string requires type cast incompatible with the CSSProperties type
		overflowX: 'visible !important' as 'visible',
	},
	// Side-aligned native embeds set overflow:auto on the extension container so wide content can
	// scroll. The resize shell is different: its only horizontal overflow is the handle chrome.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target resize shell marker to keep this exception native-embed-only
	'.extension-container:has([data-native-embed-resizer-shell="true"])': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- !important string requires type cast incompatible with the CSSProperties type
		overflowX: 'visible !important' as 'visible',
	},
	// When the center-aligned extension-owned resize chrome is visible, the editor selection/hover
	// frame must follow the native embed's explicit width. Side and wrap alignments keep their
	// existing wrapper sizing so they are not pinned to a pixel width just because the shell is active.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target extension container by an active marker owned by the native embed shell
	'.extension-container:has([data-native-embed-resizer-active="true"][data-native-embed-resizer-alignment="center"]), .extension-container:has([data-native-embed-resizer-shell="true"][data-native-embed-resizer-alignment="center"]:hover)':
		{
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- !important keeps selected editor-core width rules aligned to the active resize shell
			width: 'var(--native-embed-width) !important',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- !important overrides editor-core inline max-width while the resize chrome is visible
			maxWidth: 'var(--native-embed-width) !important',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- adjacent resize handles intentionally protrude past the frame
			overflow: 'visible !important' as 'visible',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- keep left/right handles visible when alignment rules set overflow:auto
			overflowX: 'visible !important' as 'visible',
		},
	// While a resize handle is being hovered, focused, or dragged, suppress the native embed
	// title/header chrome so the shell matches the coordinator overlay's resize-only affordance.
	// Hovering the embed body still shows the normal native embed overlay.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Header chrome belongs to the native embed frame inside this shell
	'.extension-container:has([data-native-embed-resizer-handle-active="true"]) [data-header-bar], .extension-container:has([data-native-embed-resizer-handle-active="true"]) [data-header-controls]':
		{
			opacity: 0,
			pointerEvents: 'none',
			visibility: 'hidden',
		},
	// The editor resizer paints selected node handles with the focused border color. The
	// extension-owned shell keeps visible-but-idle handles neutral to match the coordinator overlay,
	// while .is-resizing continues to show the active focused color during an actual drag.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Target editor-common resizer internals inside the native embed shell only
	'.extension-container:has([data-native-embed-resizer-active="true"]) .resizer-item:not(.is-resizing) .resizer-handle-thumb, .extension-container:has([data-native-embed-resizer-shell="true"]:hover) .resizer-item:not(.is-resizing) .resizer-handle-thumb':
		{
			background: `${token('color.border')} !important`,
		},
});
/* eslint-enable @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/ui-styling-standard/no-important-styles */
